import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input, PasswordInput } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { resendEmailConfirmation, signup } from '../../api/auth';
import { extractErrorMessage } from '../../api/client';
import { signupSchema, type SignupInput } from '../../lib/validators';

export function SignupPage() {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signup(values);
      setSubmittedEmail(values.email);
    } catch (error) {
      setFormError(extractErrorMessage(error, 'Не удалось создать аккаунт'));
    }
  });

  const handleResend = async () => {
    if (!submittedEmail) return;
    setResending(true);
    try {
      await resendEmailConfirmation(submittedEmail);
      toast.success('Письмо отправлено ещё раз');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Не удалось отправить письмо'));
    } finally {
      setResending(false);
    }
  };

  if (submittedEmail) {
    return (
      <Card className="flex flex-col items-center gap-4 p-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-bg-soft text-brand-primary">
          <Mail size={28} />
        </div>
        <h1 className="text-2xl font-semibold">Проверьте почту</h1>
        <p className="text-sm text-text-muted">
          Мы отправили письмо на{' '}
          <span className="font-medium text-text">{submittedEmail}</span>. Откройте
          ссылку из письма — email подтвердится и включится пробный тариф.
        </p>
        <Button
          variant="secondary"
          onClick={handleResend}
          loading={resending}
          fullWidth
        >
          Отправить письмо повторно
        </Button>
        <Link
          to="/login"
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          Уже подтвердили — войти
        </Link>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-text">Создать аккаунт</h1>
        <p className="mt-1 text-sm text-text-muted">
          После регистрации мы отправим письмо для подтверждения email — и включим
          пробный тариф.
        </p>
      </div>

      {formError && <ErrorBanner message={formError} />}

      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <Input
          label="Имя"
          autoFocus
          placeholder="Как к вам обращаться"
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.ru"
          error={errors.email?.message}
          {...register('email')}
        />
        <PasswordInput
          label="Пароль"
          autoComplete="new-password"
          hint="Минимум 6 символов: заглавная, строчная, цифра и спецсимвол"
          error={errors.password?.message}
          {...register('password')}
        />
        <Button
          type="submit"
          fullWidth
          loading={isSubmitting}
          disabled={!isValid}
          size="lg"
        >
          Создать аккаунт
        </Button>
      </form>

      <p className="text-center text-sm text-text-muted">
        Уже есть аккаунт?{' '}
        <Link to="/login" className="font-medium text-brand-primary hover:underline">
          Войти
        </Link>
      </p>
    </Card>
  );
}
